import { useState, useLayoutEffect, useCallback, RefObject } from 'react';

import { windowIsAvailable, debounce } from '../utils';

import useTheme from './useTheme';

/**
 * @example const breakpointActive = useBreakpoint(breakpoint);
 * @param breakpoint The string indicator for the breakpoint that should be checked for validity.
 * @param options
 * @returns breakpointActive:: A boolean indicating if the given breakpoint is active or not. If false, the breakpoint is too big for the screen size.
 */
const useBreakpoint = (
  breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  options: { defaultValue?: boolean; breakpointRef?: RefObject<Element> | undefined } = {
    defaultValue: false
  }
) => {
  const {
    base: { breakpoints }
  } = useTheme();
  const breakpointVal = breakpoints[breakpoint];
  const [matches, setMatches] = useState(
    windowIsAvailable
      ? window.matchMedia(`(min-width: ${breakpointVal})`).matches
      : !!options.defaultValue
  );

  const onResize = useCallback((e: MediaQueryListEvent) => {
    setMatches(e.matches);
  }, []);

  useLayoutEffect(() => {
    // Breakpoint handling for contained elements
    if (options.breakpointRef && options.breakpointRef.current) {
      const observer = new ResizeObserver(entries => {
        const matchesBreakpoint = entries.some(({ target, contentRect }) => {
          if (target !== options.breakpointRef?.current) return;

          return contentRect.width >= parseFloat(breakpointVal) * 16;
        });

        setMatches(matchesBreakpoint);
      });

      observer.observe(options.breakpointRef.current);

      return () => {
        observer.disconnect();
      };
    }
    // Breakpoint handling for viewport
    if (windowIsAvailable) {
      const mediaMatch = window.matchMedia(`(min-width: ${breakpointVal})`);
      const mediaMatchAvailable = 'addEventListener' in mediaMatch;
      const resizeHandler = debounce(() => {
        setMatches(window.innerWidth >= parseInt(breakpointVal, 10));
      }, 100);

      // Need to check useMatchMedia in order to support Enzyme testing
      if (mediaMatchAvailable) {
        mediaMatch.addEventListener('change', onResize);
        setMatches(mediaMatch.matches);
      } else {
        window.addEventListener('resize', resizeHandler);
        setMatches(window.innerWidth >= parseInt(breakpointVal, 10));
      }

      return () => {
        // Need to check useMatchMedia in order to support Enzyme testing
        if (mediaMatchAvailable) mediaMatch.removeEventListener('change', onResize);
        else {
          window.removeEventListener('resize', resizeHandler);
        }
      };
    }
  }, [options.breakpointRef?.current]);

  return matches;
};

export default useBreakpoint;
